// `defineCommand` and `defineQuery` (plan §4.2, §4.4). They return plain objects holding Zod schemas and metadata; no
// router is imported here. The rules the plan enforces "at definition time" are encoded in the spec types where the
// compiler can check them, and re-checked at runtime by `lintCatalog` for anything built without the compiler:
//   - availability 'cloud' needs a `cloudReason`; 'location' operations never originate cloud-only;
//   - origin 'device' needs availability 'location' and a `stream` (it is decided on a device and synced as a fact);
//   - risk 'medium' or above needs `dryRun: true`; risk 'critical' is never exposed to agents.
// Schemas describe the wire: codecs (Money, Rate, ids) are the only transforms, so every schema encodes and decodes.
import type { z } from 'zod';
import type { PermissionKey } from '../permissions/catalog.ts';
import type { ErrorDef } from './errors.ts';
import type { AnyEventDef } from './event.ts';
import type { Upcaster } from './upcast.ts';
import type { AgentExposure, AggregateKind, Audience, Availability, OperationName, Origin, Risk } from './meta.ts';

/**
 * Who may call an operation: a catalog permission, any signed-in principal of an audience (or of one of several
 * audiences: `online.submitOrder` admits a delivery partner and the Radish Direct storefront), or an explicit public mark.
 */
export type PermissionRequirement =
  | PermissionKey
  | { readonly session: Audience | readonly Audience[] }
  | { readonly public: true; readonly reviewedBy: 'CODEOWNERS' };

/** Whether a session requirement admits a principal of `audience`. */
export function sessionAdmits(requirement: { readonly session: Audience | readonly Audience[] }, audience: string): boolean {
  const s = requirement.session;
  return typeof s === 'string' ? s === audience : (s as readonly string[]).includes(audience);
}

/**
 * Whether a principal meets a catalog permission: it holds the key, and a device permission (audience 'device', KDS-P-01)
 * is met only by a device principal, whatever a token's key list says.
 */
export function holdsPermission(principal: { readonly audience: string; readonly permissions: ReadonlySet<string> }, key: string, audienceOfKey: string | undefined): boolean {
  return principal.permissions.has(key) && (audienceOfKey !== 'device' || principal.audience === 'device');
}

/** How an operation's permission reads in errors and generated docs: the key, `session:<audience>[|<audience>]` or `public`. */
export function permissionLabel(def: { readonly permission: PermissionRequirement }): string {
  const p = def.permission;
  if (typeof p === 'string') return p;
  if ('session' in p) return `session:${typeof p.session === 'string' ? p.session : p.session.join('|')}`;
  return 'public';
}

/** The aggregate stream an event-sourced command decides on. */
export interface StreamRef<I extends z.ZodType = z.ZodType> {
  readonly aggregate: AggregateKind;
  // Method syntax on purpose: a catalog holds commands of many input types, so this must stay bivariant.
  id(input: z.output<I>): string;
}

/**
 * The back-office keys an operation also needs from a back-office session (E-09): a fixed list, or a split on whether
 * one input field is given (legacy served the same rows from two endpoints in two report sections).
 */
export type BackofficeAlso =
  | readonly PermissionKey[]
  | { readonly whenInput: string; readonly given: readonly PermissionKey[]; readonly otherwise: readonly PermissionKey[] };

export interface CommandDef<
  N extends OperationName = OperationName,
  I extends z.ZodType = z.ZodType,
  O extends z.ZodType = z.ZodType,
  E extends readonly ErrorDef[] = readonly ErrorDef[],
> {
  readonly kind: 'command';
  readonly name: N;
  /** 1 unless a breaking change created a new version (both live during the supported window). */
  readonly version: number;
  readonly summary: string;
  readonly input: I;
  readonly output: O;
  /**
   * The output's schema version, stored with every recorded result so a replay decodes it (C8). Bump it when an
   * output change would stop an older stored result from decoding, and add that version's step to `outputUpcastFrom`.
   */
  readonly outputVersion: number;
  /** One step per older output version, 1 through `outputVersion - 1` (define/upcast.ts). */
  readonly outputUpcastFrom?: readonly Upcaster[];
  readonly permission: PermissionRequirement;
  /**
   * Back-office keys a back-office (user) session also needs, beyond `permission` (E-09: legacy checked these in the
   * browser only). Register and device sessions keep `permission` alone.
   */
  readonly backofficeAlso?: BackofficeAlso;
  readonly risk: Risk;
  readonly availability: Availability;
  readonly origin: Origin;
  readonly cloudReason?: string;
  readonly stream?: StreamRef<I>;
  readonly emits: readonly AnyEventDef[];
  readonly errors: E;
  /** Every command is idempotent by `commandId`; the literal is here for readers. */
  readonly idempotent: true;
  readonly dryRun: boolean;
  readonly agent: AgentExposure;
  readonly legacy?: readonly string[];
  /** The request body cap when a command carries more than the server's default 1 MiB (an image upload). */
  readonly maxBodyBytes?: number;
  /**
   * Radish staff may send this command at any location it names, acting in that location's account, from a session in
   * no account or another one (lane CM; coordinator 2026-10-15, option B). Opt-in per contract: invariant A32 lists
   * every command that sets it, so adding one is a visible, reviewed change.
   */
  readonly staffAnyLocation?: true;
  /**
   * The command runs only as another command's companion (planned by its handler's `companions`). Every direct call (a
   * REST call or the Broker, dry run included) is refused 403 `forbidden` with `permission: 'companion_only'` before the
   * bus runs it, whatever keys the caller holds (B977). Never exposed to agents. The flag guards the network dispatch
   * only: sync push admits facts, not commands, on its own trust model (a companion's fact carries no link to its lead),
   * so a companion-only command whose write a pushed fact could carry must also keep that write out of a pusher's reach
   * (`labor.supersedeShiftSwaps` writes `swaps`, a cloud-authoritative field the merge ignores from a node or device).
   */
  readonly companionOnly?: true;
}

export interface QueryDef<
  N extends OperationName = OperationName,
  I extends z.ZodType = z.ZodType,
  O extends z.ZodType = z.ZodType,
  E extends readonly ErrorDef[] = readonly ErrorDef[],
> {
  readonly kind: 'query';
  readonly name: N;
  readonly summary: string;
  readonly input: I;
  readonly output: O;
  readonly permission: PermissionRequirement;
  /**
   * Back-office keys a back-office (user) session also needs, beyond `permission` (E-09: legacy checked these in the
   * browser only). Register and device sessions keep `permission` alone.
   */
  readonly backofficeAlso?: BackofficeAlso;
  readonly availability: Availability;
  readonly cloudReason?: string;
  readonly scope: 'location' | 'account' | 'reseller' | 'platform';
  readonly errors: E;
  readonly pagination?: { readonly kind: 'cursor'; readonly maxPageSize: number };
  /** `noStore` answers `Cache-Control: no-store` instead (a merchant's review image: nothing keeps a copy). */
  readonly cache?: { readonly maxAgeSeconds: number; readonly staleWhileRevalidate?: number; readonly private: true; readonly noStore?: true };
  readonly agent: AgentExposure;
  readonly legacy?: readonly string[];
}

export type OperationDef = CommandDef | QueryDef;

type CommandPlacement<I extends z.ZodType> =
  | { readonly availability: 'location'; readonly origin: 'device'; readonly stream: StreamRef<I> }
  | { readonly availability: 'location'; readonly origin: 'any'; readonly stream?: StreamRef<I> }
  | { readonly availability: 'cloud'; readonly origin: 'any' | 'cloud'; readonly cloudReason: string };

type CommandSafety =
  | { readonly risk: 'low'; readonly dryRun: boolean; readonly agent: AgentExposure }
  | { readonly risk: 'medium' | 'high'; readonly dryRun: true; readonly agent: AgentExposure }
  | { readonly risk: 'critical'; readonly dryRun: true; readonly agent: AgentExposure & { readonly expose: false } };

type CommandSpec<N extends OperationName, I extends z.ZodType, O extends z.ZodType, E extends readonly ErrorDef[]> = Omit<
  CommandDef<N, I, O, E>,
  'kind' | 'version' | 'outputVersion' | 'availability' | 'origin' | 'cloudReason' | 'stream' | 'risk' | 'dryRun' | 'agent' | 'idempotent'
> & { readonly version?: number; readonly outputVersion?: number; readonly idempotent?: true } & CommandPlacement<I> & CommandSafety;

type QuerySpec<N extends OperationName, I extends z.ZodType, O extends z.ZodType, E extends readonly ErrorDef[]> = Omit<
  QueryDef<N, I, O, E>,
  'kind' | 'availability' | 'cloudReason'
> & ({ readonly availability: 'location' } | { readonly availability: 'cloud'; readonly cloudReason: string });

export function defineCommand<
  const N extends OperationName, I extends z.ZodType, O extends z.ZodType, const E extends readonly ErrorDef[],
>(spec: CommandSpec<N, I, O, E>): CommandDef<N, I, O, E> {
  return { ...spec, kind: 'command', version: spec.version ?? 1, outputVersion: spec.outputVersion ?? 1, idempotent: true };
}

export function defineQuery<
  const N extends OperationName, I extends z.ZodType, O extends z.ZodType, const E extends readonly ErrorDef[],
>(spec: QuerySpec<N, I, O, E>): QueryDef<N, I, O, E> {
  return { ...spec, kind: 'query' };
}
